// src/pages/supplier/BulkImportPage.jsx
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Download, RefreshCw } from 'lucide-react'
import { bulkImportApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function BulkImportPage() {
  const qc = useQueryClient()
  const fileRef = useRef()
  const [polling, setPolling] = useState(null)

  const { data, isLoading } = useQuery({ queryKey: ['bulk-imports'], queryFn: () => bulkImportApi.list() })
  const upload = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append('file', file); return bulkImportApi.upload(fd) },
    onSuccess: (res) => {
      const job = res?.data?.data
      toast.success('File uploaded! Processing…')
      qc.invalidateQueries(['bulk-imports'])
      if (job?.id) {
        const id = setInterval(async () => {
          const r = await bulkImportApi.status(job.id)
          if (['completed','failed'].includes(r?.data?.data?.status)) { clearInterval(id); setPolling(null); qc.invalidateQueries(['bulk-imports']) }
        }, 3000)
        setPolling(id)
      }
    },
    onError: e => toast.error(e.response?.data?.message || 'Upload failed'),
  })

  if (isLoading) return <PageSpinner />
  const jobs = data?.data?.data?.data || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Bulk Product Import</h1>
        <a href="/api/supplier/import/template" download>
          <Button variant="secondary" size="sm"><Download size={14} /> Download Template</Button>
        </a>
      </div>

      <Card className="mb-6">
        <CardBody>
          <div className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-pink transition-colors" onClick={() => fileRef.current?.click()}>
            <Upload size={32} className="text-muted mx-auto mb-2" />
            <p className="text-sm font-bold text-gray-900">Click to upload .xlsx / .csv</p>
            <p className="text-xs text-muted mt-1">Max file size: 10MB</p>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
              onChange={e => { if (e.target.files[0]) upload.mutate(e.target.files[0]); e.target.value = '' }} />
          </div>
          {upload.isPending && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted">
              <RefreshCw size={14} className="animate-spin" /> Uploading…
            </div>
          )}
        </CardBody>
      </Card>

      {jobs.length > 0 && (
        <Card>
          <CardHeader><span className="font-bold text-sm">Import History</span></CardHeader>
          <CardBody className="p-0">
            {jobs.map(job => (
              <div key={job.id} className="px-5 py-4 border-b border-border last:border-b-0">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={job.status} />
                      {job.total_rows > 0 && <span className="text-xs text-muted">{job.processed_rows}/{job.total_rows} rows</span>}
                    </div>
                    <p className="text-xs text-muted mt-1">{new Date(job.created_at).toLocaleString('en-IN')}</p>
                  </div>
                  <div className="text-right text-xs">
                    {job.created_count > 0 && <p className="text-green-600">+{job.created_count} created</p>}
                    {job.updated_count > 0 && <p className="text-blue-600">{job.updated_count} updated</p>}
                    {job.error_count > 0 && <p className="text-red-500">{job.error_count} errors</p>}
                  </div>
                </div>
                {job.status === 'processing' && (
                  <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-brand rounded-full transition-all" style={{ width: `${job.progress ?? 0}%` }} />
                  </div>
                )}
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  )
}
